import {
  type AgingWipReport,
  type BurndownReport,
  type ChangeRequestTrendReport,
  type CycleTimeReport,
  type DocumentStatusReport,
  type DrillDownMetric,
  type DrillDownResult,
  type EstimateAccuracyReport,
  type EvmReport,
  type FlowTimeline,
  type ObjectiveTraceReport,
  type PeriodComparisonReport,
  type PhaseGateReport,
  type ReportAvailability,
  type ReportReadSnapshot,
  type RiskBurndownReport,
  type RiskMatrixReport,
  type SlippageReport,
  type ThroughputReport,
  type UnplannedReport,
  type VelocityReport,
} from '@apm/contracts';
import { apiFetch } from '../../lib/http';

export interface ReportRange {
  readonly from: string;
  readonly to: string;
  /** ISO 8601. Bỏ trống = hiện tại. */
  readonly asOf?: string | undefined;
}

function search(params: Record<string, string | undefined>): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value) query.set(key, value);

  const text = query.toString();
  return text ? `?${text}` : '';
}

export const reportApi = {
  availability: (projectId: string) =>
    apiFetch<ReportAvailability>(`/projects/${projectId}/reports/availability`),

  flow: (projectId: string, range: ReportRange, signal?: AbortSignal) =>
    apiFetch<FlowTimeline>(
      `/projects/${projectId}/reports/flow${search({ from: range.from, to: range.to, asOf: range.asOf })}`,
      { ...(signal ? { signal } : {}) },
    ),

  drillDown: (
    projectId: string,
    query: {
      metric: DrillDownMetric;
      bucket: string;
      category?: string;
      asOf?: string;
      snapshot?: ReportReadSnapshot;
    },
  ) =>
    apiFetch<DrillDownResult>(
      `/projects/${projectId}/reports/drill-down${search({
        metric: query.metric,
        bucket: query.bucket,
        category: query.category,
        snapshotCap: query.snapshot?.cap,
        snapshotAt: query.snapshot?.computedAt,
        snapshotRevision: query.snapshot?.revision,
        asOf: query.asOf,
      })}`,
    ),

  // ── Flow metrics — APM-802 ────────────────────────────────────────────────

  cycleTime: (projectId: string, range: ReportRange) =>
    apiFetch<CycleTimeReport>(
      `/projects/${projectId}/reports/cycle-time${search({ from: range.from, to: range.to, asOf: range.asOf })}`,
    ),

  agingWip: (projectId: string, asOf?: string) =>
    apiFetch<AgingWipReport>(`/projects/${projectId}/reports/aging-wip${search({ asOf })}`),

  /** Tỉ lệ việc phát sinh theo tuần — APM-MAINT-d. Không nhận `asOf`. */
  /** Kỳ này so với kỳ trước — APM-MAINT-f, chỉ dự án bảo trì. */
  periodComparison: (projectId: string, period: string) =>
    apiFetch<PeriodComparisonReport>(
      `/projects/${projectId}/reports/period-comparison?period=${encodeURIComponent(period)}`,
    ),

  unplanned: (projectId: string, range: ReportRange) =>
    apiFetch<UnplannedReport>(
      `/projects/${projectId}/reports/unplanned${search({ from: range.from, to: range.to })}`,
    ),

  throughput: (projectId: string, range: ReportRange, bucket: 'day' | 'week' = 'week') =>
    apiFetch<ThroughputReport>(
      `/projects/${projectId}/reports/throughput${search({ from: range.from, to: range.to, asOf: range.asOf, bucket })}`,
    ),

  burndown: (projectId: string, range: ReportRange, sprintId?: string) =>
    apiFetch<BurndownReport>(
      `/projects/${projectId}/reports/burndown${search({
        from: range.from,
        to: range.to,
        asOf: range.asOf,
        sprintId,
      })}`,
    ),

  velocity: (projectId: string) =>
    apiFetch<VelocityReport>(`/projects/${projectId}/reports/velocity`),

  // ── Waterfall — APM-803, spec 16.3 ────────────────────────────────────────

  evm: (projectId: string, range: ReportRange) =>
    apiFetch<EvmReport>(
      `/projects/${projectId}/reports/evm${search({ from: range.from, to: range.to, asOf: range.asOf })}`,
    ),

  slippage: (projectId: string, range: ReportRange) =>
    apiFetch<SlippageReport>(
      `/projects/${projectId}/reports/slippage${search({ from: range.from, to: range.to, asOf: range.asOf })}`,
    ),

  phaseGates: (projectId: string, asOf?: string) =>
    apiFetch<PhaseGateReport>(`/projects/${projectId}/reports/phase-gates${search({ asOf })}`),

  // ── Chart đặc thù — APM-803, spec 16.4 ────────────────────────────────────

  riskMatrix: (projectId: string, asOf?: string) =>
    apiFetch<RiskMatrixReport>(`/projects/${projectId}/reports/risk-matrix${search({ asOf })}`),

  riskBurndown: (projectId: string, range: ReportRange) =>
    apiFetch<RiskBurndownReport>(
      `/projects/${projectId}/reports/risk-burndown${search({ from: range.from, to: range.to, asOf: range.asOf })}`,
    ),

  estimateAccuracy: (projectId: string, range: ReportRange) =>
    apiFetch<EstimateAccuracyReport>(
      `/projects/${projectId}/reports/estimate-accuracy${search({ from: range.from, to: range.to, asOf: range.asOf })}`,
    ),

  objectiveTrace: (projectId: string, asOf?: string) =>
    apiFetch<ObjectiveTraceReport>(
      `/projects/${projectId}/reports/objective-trace${search({ asOf })}`,
    ),

  crTrend: (projectId: string, range: ReportRange) =>
    apiFetch<ChangeRequestTrendReport>(
      `/projects/${projectId}/reports/cr-trend${search({ from: range.from, to: range.to, asOf: range.asOf })}`,
    ),

  documentStatus: (projectId: string, asOf?: string) =>
    apiFetch<DocumentStatusReport>(
      `/projects/${projectId}/reports/document-status${search({ asOf })}`,
    ),
};

const stamp = (range: ReportRange) => [range.from, range.to, range.asOf ?? ''] as const;

export const reportKeys = {
  availability: (projectId: string) => ['report', projectId, 'availability'] as const,
  flow: (projectId: string, range: ReportRange) =>
    ['report', projectId, 'flow', ...stamp(range)] as const,
  cycleTime: (projectId: string, range: ReportRange) =>
    ['report', projectId, 'cycle', ...stamp(range)] as const,
  agingWip: (projectId: string, asOf: string) => ['report', projectId, 'aging', asOf] as const,
  unplanned: (projectId: string, range: ReportRange) =>
    ['reports', projectId, 'unplanned', range.from, range.to] as const,

  periodComparison: (projectId: string, period: string) =>
    ['reports', projectId, 'period-comparison', period] as const,

  throughput: (projectId: string, range: ReportRange, bucket: string) =>
    ['report', projectId, 'throughput', bucket, ...stamp(range)] as const,
  burndown: (projectId: string, range: ReportRange, sprintId: string) =>
    ['report', projectId, 'burndown', sprintId, ...stamp(range)] as const,
  velocity: (projectId: string) => ['report', projectId, 'velocity'] as const,
  drillDown: (projectId: string, metric: string, bucket: string, category: string, asOf: string) =>
    ['report', projectId, 'drill', metric, bucket, category, asOf] as const,

  evm: (projectId: string, range: ReportRange) =>
    ['report', projectId, 'evm', ...stamp(range)] as const,
  slippage: (projectId: string, range: ReportRange) =>
    ['report', projectId, 'slippage', ...stamp(range)] as const,
  phaseGates: (projectId: string, asOf: string) => ['report', projectId, 'gates', asOf] as const,
  riskMatrix: (projectId: string, asOf: string) => ['report', projectId, 'risk', asOf] as const,
  riskBurndown: (projectId: string, range: ReportRange) =>
    ['report', projectId, 'risk-burndown', ...stamp(range)] as const,
  estimateAccuracy: (projectId: string, range: ReportRange) =>
    ['report', projectId, 'estimate', ...stamp(range)] as const,
  objectiveTrace: (projectId: string, asOf: string) =>
    ['report', projectId, 'objectives', asOf] as const,
  crTrend: (projectId: string, range: ReportRange) =>
    ['report', projectId, 'cr-trend', ...stamp(range)] as const,
  documentStatus: (projectId: string, asOf: string) =>
    ['report', projectId, 'documents', asOf] as const,
};
